import { Box, Skeleton } from "@mui/material";
import React from "react";
import ReactTimeAgo from "react-time-ago";

import { ImageCanvas } from "components/common/ImageCanvas";
import { getCanvasAnnotationsFromROIs } from "features/canvas/utils";
import { useAppDispatch } from "hooks/store";
import { Typography } from "new-components/typography/Typography";
import { setIsFocusModalOpen, setUnsavedAnnotations } from "store/slices/FocusModal/focusModalSlice";
import { Detector } from "types/detectors";
import { generateImageUrl } from "utils/generateImageUrl";
import { getColorText, getImageBarColor, getImageBarTitle, getOpacity, getTitleSuffix } from "utils/imageQueriesUtils";

interface IExampleImageProps {
  setCreateLabelPopoverOpen?: (setter: boolean) => void;
  detector: Detector;
}

/* eslint-disable prefer-arrow-callback */
export const ExampleImage = React.memo(function ExampleImage(props: IExampleImageProps) {
  const { setCreateLabelPopoverOpen, detector } = props;

  const dispatch = useAppDispatch();
  const imageQuery = detector.last_image_query;

  const handleImageClick = () => {
    if (setCreateLabelPopoverOpen) {
      dispatch(setUnsavedAnnotations(false));
      dispatch(setIsFocusModalOpen(true));
      setCreateLabelPopoverOpen(true);
    }
  };

  if (!imageQuery) {
    return (
      <Box style={{ position: "relative", height: "300px" }}>
        <Skeleton
          variant="rectangular"
          sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, height: "300px" }}
        />
        <Typography
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
          }}
        >
          No user-labeled examples are available
        </Typography>
      </Box>
    );
  }

  const rois = getCanvasAnnotationsFromROIs(imageQuery.current_best_prediction_rois ?? []);

  return (
    <Box onClick={handleImageClick} className="cursor-pointer">
      <Box sx={{ height: "300px", width: "100%" }}>
        <ImageCanvas
          imageUrl={generateImageUrl(imageQuery.iq_id)}
          annotations={rois}
          style={{ cursor: "pointer" }}
          iqId={imageQuery.iq_id}
        />
      </Box>
      <Box
        className="flex w-full h-[30px] px-3"
        sx={{
          alignItems: "center",
          backgroundColor: getImageBarColor(imageQuery),
          justifyContent: "space-between",
          opacity: getOpacity(imageQuery),
        }}
      >
        <Typography variant="detail" className={`${getColorText(imageQuery)}`} noWrap>
          {getImageBarTitle(imageQuery)}
          {getTitleSuffix(imageQuery, detector.mode)}
        </Typography>
        <Typography variant="detail" className={`${getColorText(imageQuery)}`} flexShrink={0}>
          <ReactTimeAgo date={new Date(imageQuery?.created_at || "")} locale="en-US" />
        </Typography>
      </Box>
    </Box>
  );
});
